import * as THREE from 'three'
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js'
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer.js'
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass.js'
import { UnrealBloomPass } from 'three/examples/jsm/postprocessing/UnrealBloomPass.js'
import GUI from 'lil-gui'

/**
 * Loader
 */
const loaderContainer = document.getElementById('loader')
const loaderBar = document.getElementById('loader-bar')
const loaderPercent = document.getElementById('loader-percent')
const canvas = document.querySelector('canvas.webgl')

const updateLoader = (progress) => {
    loaderBar.style.width = `${progress}%`
    loaderPercent.textContent = `${Math.floor(progress)}%`
}

const simulateLoading = () => {
    return new Promise((resolve) => {
        let progress = 0
        const interval = setInterval(() => {
            progress += Math.random() * 15 + 5
            if (progress >= 99) {
                progress = 99
                updateLoader(progress)
                clearInterval(interval)

                setTimeout(() => {
                    updateLoader(100)
                    setTimeout(resolve, 500)
                }, 1000)
            } else {
                updateLoader(progress)
            }
        }, 200)
    })
}

const hideLoader = () => {
    loaderContainer.classList.add('hidden')
    canvas.classList.add('loaded')
}

/**
 * Base
 */
// Debug
const gui = new GUI({ width: 360, title: 'Controls' })

// Scene
const scene = new THREE.Scene()
scene.background = new THREE.Color('#0a0a12')

/**
 * Galaxy Parameters
 */
const parameters = {}
parameters.count = 87800
parameters.size = 0.006
parameters.radius = 16.98
parameters.branches = 15
parameters.spin = 0.34
parameters.randomness = 1.611
parameters.randomnessPower = 7.3
parameters.insideColor = '#2b00ff'
parameters.outsideColor = '#206497'

let geometry = null
let material = null
let points = null

/**
 * Background Stars
 */
const createBackgroundStars = () => {
    const starsGeometry = new THREE.BufferGeometry()
    const starsCount = 2000
    const starsPositions = new Float32Array(starsCount * 3)
    const starsColors = new Float32Array(starsCount * 3)

    for (let i = 0; i < starsCount; i++) {
        const i3 = i * 3
        const radius = 50 + Math.random() * 100
        const theta = Math.random() * Math.PI * 2
        const phi = Math.acos((Math.random() * 2) - 1)

        starsPositions[i3] = radius * Math.sin(phi) * Math.cos(theta)
        starsPositions[i3 + 1] = radius * Math.sin(phi) * Math.sin(theta)
        starsPositions[i3 + 2] = radius * Math.cos(phi)

        // Random star colors - mostly white with some blue/purple tints
        const colorChoice = Math.random()
        if (colorChoice < 0.7) {
            // White stars
            starsColors[i3] = 1
            starsColors[i3 + 1] = 1
            starsColors[i3 + 2] = 1
        } else if (colorChoice < 0.85) {
            // Blue tint
            starsColors[i3] = 0.7
            starsColors[i3 + 1] = 0.8
            starsColors[i3 + 2] = 1
        } else {
            // Purple/pink tint
            starsColors[i3] = 0.9
            starsColors[i3 + 1] = 0.7
            starsColors[i3 + 2] = 1
        }
    }

    starsGeometry.setAttribute('position', new THREE.BufferAttribute(starsPositions, 3))
    starsGeometry.setAttribute('color', new THREE.BufferAttribute(starsColors, 3))

    const starsMaterial = new THREE.PointsMaterial({
        size: 0.1,
        sizeAttenuation: true,
        transparent: true,
        opacity: 0.9,
        blending: THREE.AdditiveBlending,
        vertexColors: true
    })

    const stars = new THREE.Points(starsGeometry, starsMaterial)
    scene.add(stars)

    return stars
}

createBackgroundStars()

/**
 * Galaxy Generator
 */
const generateGalaxy = () => {
    if (points !== null) {
        geometry.dispose()
        material.dispose()
        scene.remove(points)
    }

    geometry = new THREE.BufferGeometry()
    const positions = new Float32Array(parameters.count * 3)
    const colors = new Float32Array(parameters.count * 3)
    const scales = new Float32Array(parameters.count)

    const colorInside = new THREE.Color(parameters.insideColor)
    const colorOutside = new THREE.Color(parameters.outsideColor)

    for (let i = 0; i < parameters.count; i++) {
        const i3 = i * 3

        const radius = Math.random() * parameters.radius
        const branchAngle = (i % parameters.branches) / parameters.branches * Math.PI * 4
        const spinAngle = radius * parameters.spin * Math.sin(radius)

        const randomX = Math.pow(Math.random(), parameters.randomnessPower) * (Math.random() < 0.5 ? 1 : -1) * parameters.randomness
        const randomY = Math.pow(Math.random(), parameters.randomnessPower) * (Math.random() < 0.5 ? 1 : -1) * parameters.randomness * 0.5
        const randomZ = Math.pow(Math.random(), parameters.randomnessPower) * (Math.random() < 0.5 ? 1 : -1) * parameters.randomness

        positions[i3 + 0] = Math.cos(branchAngle + spinAngle) * radius + randomX
        positions[i3 + 1] = randomY
        positions[i3 + 2] = Math.sin(branchAngle + spinAngle) * radius + randomZ

        const mixedColor = colorInside.clone()
        mixedColor.lerp(colorOutside, radius / parameters.radius)

        colors[i3 + 0] = mixedColor.r
        colors[i3 + 1] = mixedColor.g
        colors[i3 + 2] = mixedColor.b

        scales[i] = Math.random()
    }

    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3))
    geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3))
    geometry.setAttribute('aScale', new THREE.BufferAttribute(scales, 1))

    material = new THREE.PointsMaterial({
        size: parameters.size,
        sizeAttenuation: true,
        depthWrite: false,
        blending: THREE.AdditiveBlending,
        vertexColors: true,
    })

    points = new THREE.Points(geometry, material)
    scene.add(points)
}

generateGalaxy()

/**
 * GUI Controls
 */
gui.add(parameters, 'count').min(100).max(150000).step(100).name('count').onFinishChange(generateGalaxy)
gui.add(parameters, 'size').min(0.001).max(0.1).step(0.001).name('size').onFinishChange(generateGalaxy)
gui.add(parameters, 'radius').min(0.01).max(30).step(0.01).name('radius').onFinishChange(generateGalaxy)
gui.add(parameters, 'branches').min(2).max(20).step(1).name('branches').onFinishChange(generateGalaxy)
gui.add(parameters, 'spin').min(-5).max(5).step(0.01).name('spin').onFinishChange(generateGalaxy)
gui.add(parameters, 'randomness').min(0).max(3).step(0.001).name('randomness').onFinishChange(generateGalaxy)
gui.add(parameters, 'randomnessPower').min(1).max(10).step(0.1).name('randomnessPower').onFinishChange(generateGalaxy)
gui.addColor(parameters, 'insideColor').name('insideColor').onFinishChange(generateGalaxy)
gui.addColor(parameters, 'outsideColor').name('outsideColor').onFinishChange(generateGalaxy)

/**
 * Sizes
 */
const sizes = {
    width: window.innerWidth,
    height: window.innerHeight
}

window.addEventListener('resize', () => {
    sizes.width = window.innerWidth
    sizes.height = window.innerHeight

    camera.aspect = sizes.width / sizes.height
    camera.updateProjectionMatrix()

    renderer.setSize(sizes.width, sizes.height)
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))

    composer.setSize(sizes.width, sizes.height)
})

/**
 * Camera
 */
const camera = new THREE.PerspectiveCamera(75, sizes.width / sizes.height, 0.1, 200)
camera.position.x = 3
camera.position.y = 5
camera.position.z = 8
scene.add(camera)

/**
 * Controls - faqat zoom (Z axis) limit
 */
const controls = new OrbitControls(camera, canvas)
controls.enableDamping = true
controls.enablePan = false
controls.minDistance = 3
controls.maxDistance = 40

/**
 * Renderer
 */
const renderer = new THREE.WebGLRenderer({
    canvas: canvas,
    antialias: true,
    powerPreference: 'high-performance'
})
renderer.setSize(sizes.width, sizes.height)
renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))
renderer.toneMapping = THREE.ACESFilmicToneMapping
renderer.toneMappingExposure = 1.5

/**
 * Post Processing - Bloom Effect
 */
const composer = new EffectComposer(renderer)
const renderPass = new RenderPass(scene, camera)
composer.addPass(renderPass)

const bloomPass = new UnrealBloomPass(
    new THREE.Vector2(sizes.width, sizes.height),
    0.5,
    0.4,
    0.85
)
composer.addPass(bloomPass)

/**
 * Animate
 */
const clock = new THREE.Clock()

const tick = () => {
    const elapsedTime = clock.getElapsedTime()

    // Rotate galaxy
    if (points) {
        points.rotation.y = elapsedTime * 0.05
    }

    controls.update()

    composer.render()

    window.requestAnimationFrame(tick)
}

/**
 * Initialize
 */
simulateLoading().then(() => {
    hideLoader()
    tick()
})
